import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, Share2 } from 'lucide-react';
import { useFrame } from '../contexts/FrameContext';
import { trackOrderButtonClick, trackStyleSelection, trackFrameSelection, trackSizeSelection, trackFunnelStep } from '../utils/analytics';
import { trackEventWithUserId, trackFunnelStepWithUserId, getStoredUserId, trackUserMilestone } from '../utils/analytics';
import { frameAvailabilityService } from '../services/frameAvailability';
import { updateWidgetCurrentImage } from '../utils/widgetSync';

export const BASE_STYLE_OPTIONS = [
  {
    name: 'Watercolor',
    description: 'Cold-pressed fine art paper',
    image: '' // Remove placeholder image
  },
  {
    name: 'Pencil',
    description: 'Hot-pressed fine art paper',
    image: '' // Remove placeholder image
  },
  {
    name: 'Oil',
    description: 'Somerset velvet fine art paper',
    image: '' // Remove placeholder image
  }
];

interface ConfigurePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onShowOrderForm?: (show: boolean) => void;
  reduceZIndex?: boolean;
  onShare?: () => void;
}

const ConfigurePanel: React.FC<ConfigurePanelProps> = ({ isOpen, onClose, onShowOrderForm, reduceZIndex = false, onShare }) => {
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(true);
  const [previousSize, setPreviousSize] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [availableFrames, setAvailableFrames] = useState<string[]>(['black', 'white', 'gold', 'natural', 'mocca']);
  const [isLoadingFrames, setIsLoadingFrames] = useState(true);
  
  // Get selectedMode from localStorage
  const [selectedMode, setSelectedMode] = useState<'home' | 'pet'>(() => {
    const saved = localStorage.getItem('selectedMode');
    return (saved === 'pet' || saved === 'home') ? saved : 'home';
  });

  const { 
    selectedFrame, 
    setSelectedFrame, 
    isChapelHouse,
    selectedStyle, 
    setSelectedStyle,
    selectedSize,
    setSelectedSize,
    propertyImage,
    styleImages
  } = useFrame();

  // Listen for changes to selectedMode in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'selectedMode' && (e.newValue === 'pet' || e.newValue === 'home')) {
        setSelectedMode(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Load frame availability on component mount
  useEffect(() => {
    const loadFrameAvailability = async () => {
      try {
        setIsLoadingFrames(true);
        console.log('🖼️ Loading frame availability...');
        
        const availableFrameIds = await frameAvailabilityService.getAvailableFrameIds();
        setAvailableFrames(availableFrameIds);
        
        console.log('✅ Frame availability loaded:', availableFrameIds);
        
        // If current selected frame is not available, switch to first available frame
        if (!availableFrameIds.includes(selectedFrame)) {
          const firstAvailable = availableFrameIds[0];
          if (firstAvailable) {
            console.log(`🔄 Selected frame ${selectedFrame} not available, switching to ${firstAvailable}`);
            setSelectedFrame(firstAvailable);
          }
        }
      } catch (error) {
        console.error('❌ Failed to load frame availability:', error);
        // Keep default frames on error
      } finally {
        setIsLoadingFrames(false);
      }
    };

    loadFrameAvailability();
  }, [selectedFrame, setSelectedFrame]);

  // Tracking wrapper functions
  const handleStyleSelection = (style: string) => {
    // Track configuration change in widget
    window.trackInteraction?.('configuration_changed', {
      property: 'style',
      from: selectedStyle,
      to: style,
      mode: selectedMode
    });
    
    setSelectedStyle(style);
    const userId = getStoredUserId();
    if (userId) {
      trackEventWithUserId('style_selected', {
        style_type: style,
        content_group1: 'configure'
      });
      trackUserMilestone('style_selected', { selected_style: style });
    } else {
      trackStyleSelection(style);
    }
    
    // Get the image URL for the selected style
    let imageUrl = '';
    if (style === 'watercolor' && propertyImage) {
      imageUrl = propertyImage;
    } else if (style === 'pencil' && styleImages?.pencil) {
      imageUrl = styleImages.pencil;
    } else if (style === 'oil' && styleImages?.oil) {
      imageUrl = styleImages.oil;
    } else if (style === 'charcoal' && styleImages?.charcoal) {
      imageUrl = styleImages.charcoal;
    } else if (style === 'chalk' && styleImages?.chalk) {
      imageUrl = styleImages.chalk;
    }
    
    // Update widget with the new image URL and current frame
    if (imageUrl) {
      const frameUrl = getFrameUrl(selectedFrame);
      console.log('🎨 Style changed, updating widget with frame:', {
        style,
        selectedFrame,
        frameUrl: frameUrl.substring(0, 50) + '...'
      });
      updateWidgetCurrentImage(imageUrl, style, frameUrl, selectedFrame);
    }
  };

  const handleFrameSelection = (frameId: string) => {
    // Track configuration change in widget
    window.trackInteraction?.('configuration_changed', {
      property: 'frame',
      from: selectedFrame,
      to: frameId,
      mode: selectedMode
    });
    
    setSelectedFrame(frameId);
    const userId = getStoredUserId();
    if (userId) {
      trackEventWithUserId('frame_selected', {
        frame_type: frameId,
        content_group1: 'configure'
      });
      trackUserMilestone('frame_selected', { selected_frame: frameId });
    } else {
      trackFrameSelection(frameId);
    }
    
    // Get the current image URL based on selected style
    let currentImageUrl = '';
    if (selectedStyle === 'watercolor' && propertyImage) {
      currentImageUrl = propertyImage;
    } else if (selectedStyle === 'pencil' && styleImages?.pencil) {
      currentImageUrl = styleImages.pencil;
    } else if (selectedStyle === 'oil' && styleImages?.oil) {
      currentImageUrl = styleImages.oil;
    } else if (selectedStyle === 'charcoal' && styleImages?.charcoal) {
      currentImageUrl = styleImages.charcoal;
    } else if (selectedStyle === 'chalk' && styleImages?.chalk) {
      currentImageUrl = styleImages.chalk;
    }
    
    // Update widget with the new frame URL
    if (currentImageUrl) {
      const frameUrl = getFrameUrl(frameId);
      console.log('🖼️ Frame changed, updating widget:', {
        frameId,
        selectedStyle,
        frameUrl: frameUrl.substring(0, 50) + '...'
      });
      updateWidgetCurrentImage(currentImageUrl, selectedStyle, frameUrl, frameId);
    }
  };

  const handleSizeSelection = (size: string) => {
    // Track configuration change in widget
    window.trackInteraction?.('configuration_changed', {
      property: 'size',
      from: selectedSize,
      to: size,
      mode: selectedMode
    });
    
    setSelectedSize(size);
    const userId = getStoredUserId();
    if (userId) {
      trackEventWithUserId('size_selected', {
        size_type: size,
        content_group1: 'configure'
      });
      trackUserMilestone('size_selected', { selected_size: size });
    } else {
      trackSizeSelection(size);
    }
    // Clear the previous size if drawer is expanded
    if (isDrawerExpanded) {
      setPreviousSize(null);
    }
  };

  const handleOrderButtonClick = () => {
    const userId = getStoredUserId();
    
    // Track order button click with current configuration
    const orderData = {
      style: selectedStyle,
      frame: selectedFrame,
      size: selectedSize,
      price: getPriceForSize(selectedSize)
    };
    
    // Get current artwork URL based on selected style
    let artworkUrl = '';
    if (selectedStyle === 'watercolor' && propertyImage) {
      artworkUrl = propertyImage;
    } else if (selectedStyle === 'pencil' && styleImages?.pencil) {
      artworkUrl = styleImages.pencil;
    } else if (selectedStyle === 'oil' && styleImages?.oil) {
      artworkUrl = styleImages.oil;
    } else if (selectedStyle === 'charcoal' && styleImages?.charcoal) {
      artworkUrl = styleImages.charcoal;
    } else if (selectedStyle === 'chalk' && styleImages?.chalk) {
      artworkUrl = styleImages.chalk;
    }
    
    // Get frame URL
    const frameUrl = getFrameUrl(selectedFrame);
    
    // Track checkout launch in widget with image URLs
    window.trackConversion?.('launched_checkout', {
      artworkUrl: artworkUrl,
      frameUrl: frameUrl,
      selectedStyle: selectedStyle,
      selectedFrame: selectedFrame,
      selectedSize: selectedSize,
      style: selectedStyle,
      frame: selectedFrame,
      size: selectedSize,
      price: orderData.price,
      mode: selectedMode
    });
    
    if (userId) {
      trackEventWithUserId('order_button_clicked', {
        ...orderData,
        value: orderData.price,
        currency: 'USD',
        content_group1: 'purchase_intent'
      });
      trackFunnelStepWithUserId('Order Button Clicked', 5, {
        style: selectedStyle,
        frame: selectedFrame,
        size: selectedSize
      });
      trackUserMilestone('order_button_clicked', {
        configured_product: `${selectedStyle}_${selectedFrame}_${selectedSize}`,
        order_value: orderData.price
      });
    } else {
      trackOrderButtonClick(orderData);
      trackFunnelStep('Order Button Clicked', 5, {
        style: selectedStyle,
        frame: selectedFrame,
        size: selectedSize
      });
    }
    
    onShowOrderForm?.(true);
  };

  // Helper function to get price for size (implement based on your pricing logic)
  const getPriceForSize = (size: string): number => {
    // Add your pricing logic here
    const prices = { XS: 47.60, S: 63.60, M: 95.60, L: 119.60 }; // Discounted prices (20% off)
    return prices[size as keyof typeof prices] || 95.60;
  };

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to check if a style has a generated image
  const hasGeneratedImage = (style: string) => {
    if (style === 'watercolor' && propertyImage) return true;
    if (style === 'pencil' && styleImages?.pencil) return true;
    if (style === 'oil' && styleImages?.oil) return true;
    if (style === 'charcoal' && styleImages?.charcoal) return true;
    if (style === 'chalk' && styleImages?.chalk) return true;
    return false;
  };

  // Effect to change the frame size when drawer is collapsed/expanded on mobile
  useEffect(() => {
    if (isMobile) {
      if (!isDrawerExpanded) {
        // Store current size before changing to L
        if (!previousSize) {
          setPreviousSize(selectedSize);
        }
        // Auto-set to 'L' size when drawer is collapsed on mobile
        setSelectedSize('L');
      } else if (previousSize) {
        // Restore previous size when drawer is expanded
        setSelectedSize(previousSize);
        // Reset previousSize after restoring
        setPreviousSize(null);
      }
    }
  }, [isDrawerExpanded, selectedSize, setSelectedSize, previousSize, isMobile]);

  // Get the combined STYLE_OPTIONS with AI-generated images when available
  const getStyleOptions = () => {
    return BASE_STYLE_OPTIONS.map(style => {
      const styleName = style.name.toLowerCase();
      let imageUrl = '';
      let isGenerating = false;

      // Use generated images if available
      if (styleName === 'watercolor' && propertyImage) {
        imageUrl = propertyImage;
      } else if (styleName === 'pencil' && styleImages && styleImages.pencil) {
        imageUrl = styleImages.pencil;
      } else if (styleName === 'oil' && styleImages && styleImages.oil) {
        imageUrl = styleImages.oil;
      } 
      // Check if image is still generating (null but should exist)
      else if (styleName === 'pencil' && styleImages && styleImages.pencil === null) {
        isGenerating = true;
      } else if (styleName === 'oil' && styleImages && styleImages.oil === null) {
        isGenerating = true;
      }
      // Special case: Chapel Hill address with specific images
      else if (isChapelHouse) {
        if (styleName === 'watercolor') {
          imageUrl = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Watercolor.png';
        } else if (styleName === 'pencil') {
          imageUrl = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Pencil.png';
        } else if (styleName === 'oil') {
          imageUrl = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Oil.png';
        }
      }
      // For all other cases, show as generating (grey box)
      else {
        isGenerating = true;
      }

      return {
        ...style,
        image: imageUrl,
        isGenerating
      };
    });
  };

  const STYLE_OPTIONS = getStyleOptions();

  // Get the image URL for the currently selected style
  const getSelectedStyleImage = () => {
    if (selectedStyle === 'watercolor' && propertyImage) {
      return propertyImage;
    } else if (selectedStyle === 'pencil' && styleImages?.pencil) {
      return styleImages.pencil;
    } else if (selectedStyle === 'oil' && styleImages?.oil) {
      return styleImages.oil;
    } else if (selectedStyle === 'charcoal' && styleImages?.charcoal) {
      return styleImages.charcoal;
    } else if (selectedStyle === 'chalk' && styleImages?.chalk) {
      return styleImages.chalk;
    }

    // Special case: Chapel Hill address with specific images
    if (isChapelHouse) {
      switch (selectedStyle) {
        case 'watercolor':
          return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Watercolor.png';
        case 'pencil':
          return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Pencil.png';
        case 'oil':
          return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Oil.png';
        default:
          return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Ian_Watercolor.png';
      }
    }

    return null; // No image available
  };

  const FRAME_OPTIONS = [
    { 
      id: 'black', 
      color: '#000000', 
      name: 'Black Wood',
      subcategoryId: 105001,
      url: 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Black_M.png'
    },
    { 
      id: 'mocca', 
      color: '#8B6E5B', 
      name: 'Mocca Wood',
      subcategoryId: 105012,
      url: 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Mocca_M.png'
    },
    { 
      id: 'white', 
      color: '#FFFFFF', 
      name: 'White Wood',
      subcategoryId: 105002,
      url: 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//White_M.png'
    },
    { 
      id: 'gold', 
      color: '#D4AF37', 
      name: 'Gold Wood',
      subcategoryId: 105011,
      url: 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Gold_M.png'
    },
    { 
      id: 'natural', 
      color: '#F5EFE6', 
      name: 'Natural Wood',
      subcategoryId: 105008,
      url: 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Natural_M.png'
    },
  ];

  const SIZE_OPTIONS = [
    { 
      label: 'XS',
      displayLabel: 'S', // User sees "S"
      size: '7×5', 
      frameSize: '12×10', 
      price: 59.50,
      matSize: 2
    },
    { 
      label: 'S', 
      displayLabel: 'M', // User sees "M"
      size: '12×8', 
      frameSize: '14×18', 
      price: 79.50,
      matSize: 2.5
    },
    { 
      label: 'M', 
      displayLabel: 'L', // User sees "L"
      size: '17×11', 
      frameSize: '24×18', 
      price: 119.50,
      matSize: 3
    },
    { 
      label: 'L', 
      displayLabel: 'XL', // User sees "XL"
      size: '24×18', 
      frameSize: '32×26', 
      price: 149.50,
      matSize: 3.5
    },
  ];

  // Get the frame URL based on the selected frame
  const getFrameUrl = (frameId: string) => {
    switch (frameId) {
      case 'gold':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Gold_M.png';
      case 'mocca':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Mocca_M.png';
      case 'natural':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Natural_M.png';
      case 'white':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//White_M.png';
      case 'black':
      default:
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Black_M.png';
    }
  };

  const toggleDrawerExpand = () => {
    setIsDrawerExpanded(!isDrawerExpanded);
  };

  // Get the price for the selected size
  const getSelectedPrice = () => {
    const sizeOption = SIZE_OPTIONS.find(option => option.label === selectedSize);
    return sizeOption ? sizeOption.price : 99.99; // Default to medium price if not found
  };

  if (!isOpen) return null;

  const baseZIndex = reduceZIndex ? 'z-10' : 'z-[200]';

  return (
    <div className={`fixed inset-0 pointer-events-none ${baseZIndex}`}>
      <div 
        className={`fixed bg-white transform transition-transform duration-300 pointer-events-auto shadow-lg overflow-hidden
          ${isMobile 
            ? 'bottom-0 left-0 right-0 max-h-[85vh] rounded-t-[32px] ' + (isOpen ? 'translate-y-0' : 'translate-y-full')
            : 'inset-y-0 left-0 w-full max-w-[320px] rounded-none shadow-[1px_0_3px_rgba(0,0,0,0.1)] ' + (isOpen ? 'translate-x-0' : '-translate-x-full')
        }`}
        style={isMobile && !isDrawerExpanded ? {
          // When collapsed on mobile, just show a small part of the drawer
          transform: 'translateY(calc(100% - 90px))'
        } : {}}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Top corner squares - only on desktop side panel */}
          <div className="hidden md:block absolute top-0 left-0 w-[0.9rem] h-10 bg-white z-[201]"></div>
          <div className="hidden md:block absolute top-0 right-[1px] w-[0.9rem] h-10 bg-white z-[201]"></div>
          
          {/* Bottom corner squares for all screen sizes - positioned above panel */}
          <div className="absolute bottom-0 left-0 w-[1.4rem] md:w-[0.9rem] h-10 bg-white z-[201]"></div>
          <div className="absolute bottom-0 right-[1px] w-[1.4rem] md:w-[0.9rem] h-10 bg-white z-[201]"></div>

          {/* Mobile Handle - hidden for now */}
          <div className="pb-0 flex justify-center md:hidden mt-[8px] max-[479px]:mt-[5px] opacity-0 pointer-events-none">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          {/* Logo Section */}
          {!isMobile && <div className="flex flex-col pt-8 md:pt-14 pb-4 px-6">
            <img 
              src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
              alt="Framemyhome"
              className="w-[175px] md:w-[200px] h-auto object-contain select-none"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
            <p className="text-[12px] text-black mt-2 max-w-[200px]">
              {selectedMode === 'pet' ? 'Frame your pet in 3 simple clicks' : 'Frame your home in 3 simple clicks'}
            </p>
          </div>}

          {/* Main content - only visible when expanded on mobile */}
          {(isDrawerExpanded || !isMobile) && (
            <div className="flex-1 overflow-y-auto px-8 max-[479px]:px-6 md:px-4 pb-0 min-h-0 mt-[0.2rem] max-[479px]:mt-[5px] md:mt-2">
              <div className="flex flex-col md:gap-6 md:h-full md:justify-center">
                {/* Styles Section */}
                <div className="pb-[0.1rem] md:pb-6 border-b border-gray-200 mb-2 md:mb-0">
                  <div className={`${isMobile ? 'flex gap-5 mt-2 max-[479px]:mt-0' : 'space-y-1'}`}>
                    {STYLE_OPTIONS.map((style) => (
                      <div
                        key={style.name}
                        onClick={() => !style.isGenerating && handleStyleSelection(style.name.toLowerCase())}
                        className={`group ${isMobile 
                         ? 'flex-1 flex flex-col items-center gap-0.5 pb-[0.1rem] pt-2'
                          : 'flex gap-3 p-2'} transition-all ${
                          style.isGenerating 
                            ? 'cursor-not-allowed opacity-60' 
                            : 'cursor-pointer'
                        } ${
                          selectedStyle === style.name.toLowerCase()
                            ? (isMobile ? 'opacity-100' : 'border border-black bg-gray-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]')
                            : (isMobile ? 'opacity-100' : 'hover:bg-gray-50')
                        }`}
                      >
                        <div className={`${isMobile ? 'w-full aspect-[3/2]' : 'w-20 flex-shrink-0'} overflow-hidden rounded-sm border-2 border-white ${
                          selectedStyle === style.name.toLowerCase() ? 'ring-1 ring-black' : 'group-hover:ring-1 group-hover:ring-black/20'
                        } transition-all relative`}>
                          <div className="aspect-[3/2] relative">
                            {style.isGenerating ? (
                              <div className="absolute inset-0 bg-gray-200 flex flex-col items-center justify-center">
                                <Loader2 className="w-4 h-4 animate-spin text-gray-400 mb-1" />
                                <span className="text-xs text-gray-400 animate-pulse-slow">Creating</span>
                              </div>
                            ) : style.image ? (
                              <img 
                                src={style.image}
                                alt={style.name}
                                className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
                                draggable="false"
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                              />
                            ) : (
                              <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
                                <span className="text-xs text-gray-400">No Image</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {isMobile ? (
                          <span className="text-[11px] font-medium text-gray-900">
                            {style.name.split(' ')[0]}
                          </span>
                        ) : (
                          <div className="flex flex-col justify-center">
                            <h3 className="text-[13px] font-medium text-gray-900 mb-2">
                              {style.name}
                            </h3>
                            <p className="text-[11px] text-gray-500">
                              {style.description}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mobile Divider */}
                <div className="hidden my-2 h-px bg-gray-200" />

                {/* Frame and Size Options Container */}
                <div className="sm:flex md:block sm:gap-4">
                  {/* Frame Selection Section */}
                  <div className="pb-[0.1rem] md:pb-6 border-b sm:border-b-0 md:border-b sm:border-r md:border-r-0 border-gray-200 sm:flex-1 sm:pr-4 md:pr-0">
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-[4.5rem] relative">
                        {/* Selected Image Behind Frame */}
                        {getSelectedStyleImage() && (
                          <div className="absolute inset-[20%] overflow-hidden">
                            <img
                              src={getSelectedStyleImage()!}
                              alt={`${selectedStyle} style`}
                              className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                              draggable="false"
                              onContextMenu={(e) => e.preventDefault()}
                              onDragStart={(e) => e.preventDefault()}
                            />
                          </div>
                        )}
                        {/* Frame Overlay */}
                        <img
                          src={getFrameUrl(selectedFrame)}
                          alt={FRAME_OPTIONS.find(f => f.id === selectedFrame)?.name}
                          className="relative w-full h-full object-contain select-none pointer-events-none"
                          draggable="false"
                          onContextMenu={(e) => e.preventDefault()}
                          onDragStart={(e) => e.preventDefault()}
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <h3 className="text-[13px] font-medium text-gray-900">
                            {FRAME_OPTIONS.find(f => f.id === selectedFrame)?.name}
                          </h3>
                          <span className="text-[13px] text-gray-500">
                            {SIZE_OPTIONS.find(option => option.label === selectedSize)?.frameSize.replace('×', '" x ')}"
                          </span>
                        </div>
                        <p className="hidden md:block text-[11px] text-gray-500">
                          0.875" frame with {SIZE_OPTIONS.find(option => option.label === selectedSize)?.matSize}" mat
                        </p>
                        <div className="flex gap-2 mt-2">
                          {isLoadingFrames ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                              <span className="text-xs text-gray-400">Loading frames...</span>
                            </div>
                          ) : (
                            FRAME_OPTIONS.filter(frame => availableFrames.includes(frame.id)).map((frame) => (
                              <button
                                key={frame.id}
                                onClick={() => handleFrameSelection(frame.id)}
                                className={`w-6 h-6 rounded-[20px] border-2 transition-all ${
                                  selectedFrame === frame.id
                                    ? '[--tw-border-opacity:0.2] border-black ring-1 ring-offset-1 ring-black/50'
                                    : 'border-gray-200'
                                }`}
                                style={{
                                  backgroundColor: frame.color
                                }}
                                data-white={frame.id === 'white' || undefined}
                                title={`${frame.name} - Available`}
                              />
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Size Selection */}
                  <div className="pb-[0.1rem] md:pb-6 sm:flex-1 sm:pl-4 md:pl-0 md:mt-4">
                    <div className="flex pt-2 md:pt-0">
                      {SIZE_OPTIONS.map((option, index) => (
                        <button
                          key={option.size}
                          onClick={() => handleSizeSelection(option.label)}
                          className={`flex-1 flex flex-col items-center justify-center p-1 transition-all ${
                            selectedSize === option.label
                              ? 'border border-black bg-gray-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)] rounded-sm z-10' 
                              : index === 0
                              ? 'border-l border-t border-b border-gray-200 hover:border-gray-300'
                              : index === SIZE_OPTIONS.length - 1
                              ? 'border-r border-t border-b border-gray-200 hover:border-gray-300'
                              : 'border-t border-b border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-[16px] font-medium text-gray-900 mb-1">
                            {option.displayLabel}
                          </span>
                          <div className="flex flex-col items-center gap-0">
                            <span className="text-[9px] text-gray-400 line-through leading-tight">
                              ${option.price.toFixed(2)}
                            </span>
                            <span className="text-[12px] text-green-600 font-medium">
                              ${(option.price * 0.80).toFixed(2)}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Checkout Section - always visible */}
          <div className="border-t border-gray-100 max-[479px]:border-t-0 px-8 py-2 max-[479px]:px-6 max-[479px]:pb-3 md:px-4 bg-white mt-auto space-y-2">
            <button 
              className="w-full bg-black text-white py-2.5 px-4 font-medium hover:bg-gray-800 transition-colors rounded-lg"
              onClick={handleOrderButtonClick}
            >
              Proceed to Checkout
            </button>
            
            {onShare && (
              <button 
                className="w-full bg-white text-black py-1.5 px-4 font-medium border border-black hover:bg-gray-50 transition-colors rounded-lg flex items-center justify-center gap-2"
                onClick={onShare}
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurePanel;